/**
 * delete record (owner grill 2026-10-06, olympus-functions `.scratch/delete-record-block/spec.md`).
 * Deletes ONE record from the database the platform's way: it disappears for everyone, the row and its
 * links stay so the Zavo team can restore it. Not the same as "delete entity" / "delete from entity list",
 * which only take an item out of a list held on the page. No Angular imports — see people-blocks.ts.
 */
import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';

const COLOUR = '#db2777';

export function registerRecordBlocks(): void {
  (Blockly as any).Blocks['delete_record'] = {
    init: function () {
      this.appendValueInput('RECORD').setCheck('ENTITY').appendField('delete record');
      this.appendDummyInput()
        .appendField(new (Blockly as any).FieldCheckbox('TRUE'), 'ASK')
        .appendField('ask first');
      this.setInputsInline(false);
      this.setOutput(true, 'String');
      this.setColour(COLOUR);
      this.setTooltip(
        "Deletes this record from the database for everyone — someone whose role may delete it, an admin or the builder. " +
        "Its own list rows go with it; records that pointed at it show it blank. People and Zavo's own records can't be deleted here. " +
        "Returns 'success', 'cancelled' or an error.");
      this.setHelpUrl('');
    },
  };
  (javascriptGenerator as any).forBlock['delete_record'] = function (block: any) {
    const ask = block.getFieldValue('ASK') === 'FALSE' ? 'false' : 'true';
    const recConn = block.getInput('RECORD')?.connection;
    const recChild = recConn?.targetBlock?.();
    const recCode = (javascriptGenerator as any).valueToCode(block, 'RECORD', (javascriptGenerator as any).ORDER_ATOMIC) || '';
    // Fix round 2 (owner grill, 2026-10-06): an unpicked get-entity block's VAR field defaults to the
    // literal 'NAN_ENTITY_VARIABLE' (see update_entity's own guard against it, query-helper.ts ~1178) —
    // left alone, valueToCode hands that string straight back as the CODE, so it lands as a bare,
    // non-existent identifier in the generated JS and throws a ReferenceError at runtime. Treat it the
    // same as nothing connected at all: pass null, so deleteRecordFromVariable answers "Pick a record to
    // delete first." instead of crashing.
    if (recCode === 'NAN_ENTITY_VARIABLE' || recCode === '') {
      return [`await this.deleteRecordFromVariable(null, ${ask})`, (javascriptGenerator as any).ORDER_ATOMIC];
    }
    // Fix round 1 (owner grill, 2026-10-06): RECORD's 'ENTITY' check also accepts a row picked straight
    // out of an entity list — set_entity_variable_by_index_of_entity_list outputs 'ENTITY' too, but its
    // code is the RAW row (`<listVar>.data[i]`), not get_entity_variable's `{ entityId, data }` wrapper.
    // Unwrapped, deleteRecordFromVariable sees no entityId/data.id and refuses with "Pick a record to
    // delete first" — misleading, since picking a row out of a Get Entity list is the most common way to
    // reach a record to delete (unlike AOB rows, these ARE top-level records; the server still refuses
    // anything that isn't). Build the wrapper the runtime expects from the list block's OWN input: its
    // token always ends in '.data' (customBlockForGetEntityListVariable / the ENTITY_LIST_VARIABLE
    // convention in variable-registry.service.ts) because the raw page variable carries `{ entityId,
    // entityName, data: [...] }` — swap '.data' for '.entityId' on that SAME object to read its id.
    if (recChild?.type === 'set_entity_variable_by_index_of_entity_list') {
      const list = (javascriptGenerator as any).valueToCode(recChild, 'VALUE', (javascriptGenerator as any).ORDER_ATOMIC) || '';
      const entityIdExpr = list.endsWith('.data') ? list.slice(0, -'.data'.length) + '.entityId' : 'null';
      const rec = `{ entityId: ${entityIdExpr}, data: ${recCode} }`;
      return [`await this.deleteRecordFromVariable(${rec}, ${ask})`, (javascriptGenerator as any).ORDER_ATOMIC];
    }
    return [`await this.deleteRecordFromVariable(${recCode}, ${ask})`, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}
